import every from 'lodash/every';
import isFinite from 'lodash/isFinite';
import isFunction from 'lodash/isFunction';
const EARTH_RADIUS = 6371.0; // in Km
const ZOOM_MAX = 17;
//Convert degress to radians
const toRadians = (degrees) => degrees * Math.PI/180;

//Convert radians to degrees
const toDegrees = (radians) => radians * 180 / Math.PI;
interface PointShorthand {
    lat: number | (() => number);
    lng: number | (() => number);
}
const isPointShorthand = (p: any): p is PointShorthand => (p && p.lat && p.lng);

interface PointLonghand {
    latitude: number;
    longitude: number;
}
const isPointLonghand = (p: any): p is PointLonghand => (p && p.latitude && p.longitude);
type Point = PointLonghand | PointShorthand | Coord;
const isPoint = (p: any): p is Point => (p && isPointLonghand(p) || isPointShorthand(p) || p instanceof Coord);
import isNumber from 'lodash/isNumber';
class Coord {
    latitude: number;
    longitude: number;
    constructor(lat: number, lng: number);
    constructor(point: Point);
    constructor(PointObjOrLat, lng?) {
        let latitude, longitude;
        if (PointObjOrLat instanceof Coord) {
            return PointObjOrLat;
        } if (isPointLonghand(PointObjOrLat)) {
            latitude = PointObjOrLat.latitude;
            longitude = PointObjOrLat.longitude;
        } else if(isPointShorthand(PointObjOrLat)) {
            latitude = PointObjOrLat.lat;
            longitude = PointObjOrLat.lng;
            if (isFunction(latitude)) latitude = latitude();
            if (isFunction(longitude)) longitude = longitude();
        } else if(isNumber(PointObjOrLat) && isNumber(lng)) {
            [ latitude, longitude ] = [PointObjOrLat, lng];
        } else throw new TypeError(JSON.stringify({PointObjOrLat, lng}));
        if (!isFinite(latitude) || !isFinite(longitude)) throw new TypeError();
        this.latitude = latitude;
        this.longitude = longitude;
    }

    get lat() { return this.latitude; }
    get lng() { return this.longitude; }
    get latr() { return toRadians(this.longitude); }
    get lngr() { return toRadians(this.longitude); }

    distanceTo(other: Coord, miles=true) {
        // cosine distance
        const other_latr = toRadians(other.lat);
        const other_lngr = toRadians(other.lng);
        const { sin, cos, acos } = Math;
	      const Δlng = toRadians(other.lng-this.lng);
	      let distance = acos(
            sin(this.latr)*sin(other_latr) + cos(this.latr)*cos(other_latr) * cos(Δlng)
        ) * EARTH_RADIUS;
	      if (miles) distance = distance/1.609344;
	      return distance;
    }
}

interface BoundsShorthand {n: number; e: number; s: number; w: number;}
const isBoundsShorthand = (b: any): b is BoundsShorthand => every([b.n, b.e, b.s, b.w], isFinite);
interface BoundsLonghand {north: number; east: number; south: number; west: number;}
const isBoundsLonghand = (b: any): b is BoundsLonghand => every([b.north, b.east, b.south, b.west, isFinite]);
interface GoogleBounds {getNorthEast: () => Coord; getSouthWest: () => Coord;}
const isGoogleBounds = (b: any):b is GoogleBounds =>(isFunction(b.getNorthEast) && isFunction(b.getSouthWest));
interface BoundsCorners { northEast: Coord; northWest: Coord; }
interface BoundsCorners {ne: Coord; nw: Coord;}
class Bounds {
    northEast: Coord;
    southWest: Coord;
    static fromPoints(points) {
        return findBounds(points);
    }
    constructor(ne, sw);
    constructor(n, e, s, w);
    constructor({n, e, s, w});
    constructor({north, east, south, west});
    constructor({getNorthEast, getSouthWest});
    constructor(nesw: number|Point|GoogleBounds|BoundsShorthand|BoundsLonghand, e_sw?: number|Point, s?: number, w?: number) {
        let north, east, south, west;
        if (every([nesw, e_sw, s, w], isFinite)) {
            this.northEast = new Coord(nesw as number, e_sw as number);
            this.southWest = new Coord(s, w);
        } else if (isPoint(e_sw) && isPoint(nesw)) {
            this.northEast = new Coord(nesw);
            this.southWest = new Coord(e_sw);
        } else if (isGoogleBounds(nesw)) {
            this.northEast = new Coord (nesw.getNorthEast());
            this.southWest = new Coord (nesw.getSouthWest());
        } else if (isBoundsLonghand(nesw)) {
            this.northEast = new Coord(nesw.north, nesw.east)
            this.southWest = new Coord(nesw.south, nesw.west)
        } else if (isBoundsShorthand(nesw)) {
            this.northEast = new Coord(nesw.n, nesw.e)
            this.southWest = new Coord(nesw.s, nesw.w)
        }
    }

    center() {
        return new Coord((this.n + this.s)/2,
                         (this.e + this.w)/2);
    }

    contains(point) {
        const nesw = {
            n: toRadians(this.northEast.lat),
            e: toRadians(this.northEast.lng),
            s: toRadians(this.southWest.lat),
            w: toRadians(this.southWest.lng)
        };
        const lng = toRadians(point.lng);
        const lat = toRadians(point.lat);

        return (inRange(lat, nesw.n, nesw.s) && inRange(lng, nesw.e, nesw.w));
    }

    get ne() { return this.northEast; }
    get sw() { return this.southWest }
    get n() { return this.northEast.lat; }
    get e() { return this.northEast.lng; }
    get s() { return this.southWest.lat; }
    get w() { return this.southWest.lng; }
    get north() { return this.n; }
    get east() { return this.e; }
    get south() { return this.s; }
    get west() { return this.w; }
    getNorthEast() { return this.northEast; }
    getSouthWest() { return this.southWest; }
}

import inRange from 'lodash/inRange';
function findBounds(points) {
    // Initialize to extremes
    let n = 90;
	  let e = 180;
	  let s = -90;
	  let w = -180;
    // Narrow down from extremes
    points.forEach((point) => {
        const p = new Coord(point);
		    n = Math.min(n, p.latitude);
		    s = Math.max(s, p.latitude);
		    e = Math.min(e, p.longitude);
		    w = Math.max(w, p.longitude);
    });
	  //if (!every([n,e,s,w], isFinite)) throw("Invalid Value."); // implied by Coord?
	  return new Bounds(n, e, s, w);
}
/**
 * Calculates center coordinates of a set of results.
 * @param subset
 * @returns {Coord} Geographic center of subset
 */
const find_bounds_center = (bounds) => ({
    lat:(bounds.n + bounds.s)/2,
    lng: (bounds.e + bounds.w)/2,
});


//Convert lat coordinate to radian coordinate?
const lat2Rad = (lat) => {
    const sin = Math.sin(toRadians(lat));
	  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
	  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
}

// return radius in meters?
// TODO: use North point and East point instead of North-East point
function map_radius(map) {
	  const bounds = map.getBounds();
	  if (!bounds) return 3000;
	  const center = new Coord(bounds.getCenter());
	  const ne = new Coord(bounds.getNorthEast());
	  // return distance in meters
	  return (center.distanceTo(ne) * 1000);
}

const find_current_zoom = (mapPx, worldPx, fraction) => (
    Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2)
);

function getZoomLevelForBounds(bounds, mapDim) {
    // http://stackoverflow.com/questions/6048975/google-maps-v3-how-to-calculate-the-zoom-level-for-a-given-bounds
	  // https://gist.github.com/konforti/0d15db2b3d75bf215df0
	  const WORLD_DIM = { height: 256, width: 256 };

    const { ne, sw } = bounds;
    const { abs, PI } = Math
	  const latFraction = abs(lat2Rad(ne.lat) - lat2Rad(sw.lat)) / PI;
	  const lngDiff = abs(ne.lng - sw.lng);
	  const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;
	  const latZoom = find_current_zoom(mapDim.height, WORLD_DIM.height, latFraction);
	  const lngZoom = find_current_zoom(mapDim.width, WORLD_DIM.width, lngFraction);

	  // if invalid, probably a zero distance bounds (ie, because of just one item)
	  // so use max zoom.
	  if (!every([latZoom, lngZoom], isFinite)) return ZOOM_MAX;

	  return Math.min(latZoom, lngZoom, ZOOM_MAX);
}

export { Coord, getZoomLevelForBounds, Bounds };



// WEBPACK FOOTER //
// ./src/app/models/Location/mapUtils.ts